"use client";

import * as React from "react";
import { Flex, Strong } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { AlertDialog, Button } from "./elements.js";
import {
  ActiveSession,
  getSessionsQueryKey,
  useRevokeSession,
} from "../api/endpoint.js";
import { useQueryClient } from "@tanstack/react-query";
import { SaveButton } from "./save-button.js";
import { parseUserAgent } from "./utils.js";
import { Translation } from "./i18n/translation.js";

interface LogoutDialogProps extends AlertDialog.RootProps {
  children?: ReactNode;
  session: ActiveSession;
}

export function LogoutDialog({
  children,
  session,
  open,
  onOpenChange,
  ...props
}: LogoutDialogProps) {
  const client = useQueryClient();
  const userAgent = parseUserAgent(session.userAgent);
  const device = userAgent.pretty;

  const revokeSession = useRevokeSession();

  const handleDone = React.useCallback(() => {
    onOpenChange?.(false);

    client.invalidateQueries({
      queryKey: getSessionsQueryKey(),
      exact: false,
    });
  }, [onOpenChange, client]);

  const onSubmitForm = () => {
    revokeSession.mutate({
      sessionId: session.id,
    });
  };

  return (
    <AlertDialog.Root open={open} onOpenChange={onOpenChange} {...props}>
      <AlertDialog.Content maxWidth="480px">
        <AlertDialog.Title>
          <Translation
            defaultMessage="Sign out of device?"
            id="Jic5nB"
            description="Dialog title asking to confirm signing out of a device"
          />
        </AlertDialog.Title>
        <AlertDialog.Description>
          <Translation
            defaultMessage="You will be signed out of {device}."
            id="diFnRK"
            description="Description explaining the user will be signed out of a specific device"
            values={{ device: <Strong>{device}</Strong> }}
          />
        </AlertDialog.Description>

        <Flex gap="3" justify="end" mt="5" asChild>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmitForm();
            }}
          >
            <AlertDialog.Cancel>
              <Button
                variant="secondary"
                disabled={revokeSession.isPending || revokeSession.isSuccess}
              >
                <Translation
                  defaultMessage="Cancel"
                  id="KjY2RX"
                  description="Button to cancel the logout action"
                />
              </Button>
            </AlertDialog.Cancel>

            <SaveButton
              asChild
              loading={revokeSession.isPending}
              done={revokeSession.isSuccess}
              onDone={handleDone}
            >
              <Button variant="destructive" type="submit">
                <Translation
                  defaultMessage="Sign out"
                  id="tRJt/S"
                  description="Button to confirm signing out of device"
                />
              </Button>
            </SaveButton>
          </form>
        </Flex>
      </AlertDialog.Content>
    </AlertDialog.Root>
  );
}
